import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plug, X, Settings, Search, AlertCircle, Lock, Eye, EyeOff,
  Save, RefreshCw, Zap, TestTube, RotateCcw, Webhook,
  Filter, Grid3X3, List, ShieldCheck, Activity, Plus, Trash2,
  Bell, CheckCircle2, XCircle, Loader2, Wifi, WifiOff, Info,
  Copy, ExternalLink, Edit3, Crown, Package, AlertTriangle,
  Star, Layers, EyeOff as EyeOffIcon, Eye as EyeIcon,
} from 'lucide-react';
import { integrationsController } from '../../controllers/integrationsController';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const CATEGORY_META = {
  All:                  { icon: '🔌', color: '#6366f1' },
  Communication:        { icon: '💬', color: '#06b6d4' },
  Payment:              { icon: '💳', color: '#10b981' },
  CRM:                  { icon: '🎯', color: '#f59e0b' },
  Analytics:            { icon: '📊', color: '#8b5cf6' },
  'Cloud Storage':      { icon: '☁️', color: '#3b82f6' },
  'Email Marketing':    { icon: '📧', color: '#ec4899' },
  'Social Media':       { icon: '📱', color: '#f97316' },
  Accounting:           { icon: '💰', color: '#14b8a6' },
  'Project Management': { icon: '✅', color: '#84cc16' },
  Calendar:             { icon: '📅', color: '#a855f7' },
  Other:                { icon: '🔧', color: '#64748b' },
};
const VALID_CATEGORIES = Object.keys(CATEGORY_META).filter(c => c !== 'All');
const SENSITIVE_KEYS   = ['secret', 'password', 'token', 'key', 'private', 'auth'];
const isSensitive      = k => SENSITIVE_KEYS.some(s => k.toLowerCase().includes(s));
const SYNC_FREQ        = ['manual', 'hourly', 'daily', 'weekly'];

// ─────────────────────────────────────────────────────────────────────────────
// UTILS
// ─────────────────────────────────────────────────────────────────────────────
const Spinner = ({ size = 16, color = 'currentColor' }) => (
  <Loader2 size={size} color={color} style={{ animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
);

const Badge = ({ text, color, bg }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '2px 9px', borderRadius: 20, fontSize: 10, fontWeight: 700,
    color, background: bg, letterSpacing: '0.3px', whiteSpace: 'nowrap',
  }}>{text}</span>
);

const HealthDot = ({ status }) => {
  const map = { healthy: '#22c55e', warning: '#f59e0b', critical: '#ef4444', disabled: '#cbd5e1' };
  return (
    <span style={{
      width: 7, height: 7, borderRadius: '50%',
      background: map[status] || map.disabled,
      display: 'inline-block', flexShrink: 0,
      boxShadow: status === 'healthy' ? '0 0 0 3px rgba(34,197,94,0.2)' : 'none',
    }} />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// TOAST
// ─────────────────────────────────────────────────────────────────────────────
const ToastBox = ({ toasts, remove }) => (
  <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8 }}>
    {toasts.map(t => (
      <div key={t.id} style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '12px 16px', borderRadius: 12, minWidth: 290, maxWidth: 380,
        background: t.type === 'success' ? '#0f172a' : t.type === 'error' ? '#7f1d1d' : '#1e293b',
        color: '#f1f5f9', fontSize: 13, fontWeight: 500,
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)', animation: 'slideIn 0.2s ease',
      }}>
        {t.type === 'success' ? <CheckCircle2 size={14} color="#4ade80" />
          : t.type === 'error' ? <XCircle size={14} color="#f87171" />
          : <Info size={14} color="#60a5fa" />}
        <span style={{ flex: 1 }}>{t.message}</span>
        <button onClick={() => remove(t.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 0, display: 'flex' }}>
          <X size={12} />
        </button>
      </div>
    ))}
  </div>
);

const useToast = () => {
  const [toasts, setToasts] = useState([]);
  const add = useCallback((type, message) => {
    const id = Date.now();
    setToasts(p => [...p, { id, type, message }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 4000);
  }, []);
  const remove = useCallback(id => setToasts(p => p.filter(t => t.id !== id)), []);
  return { toasts, toast: { success: m => add('success', m), error: m => add('error', m), info: m => add('info', m) }, remove };
};

// ─────────────────────────────────────────────────────────────────────────────
// DELETE CONFIRM MODAL
// ─────────────────────────────────────────────────────────────────────────────
const DeleteModal = ({ integration, onClose, onConfirm, loading }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, backdropFilter: 'blur(4px)' }} onClick={onClose}>
    <div style={{ background: '#fff', borderRadius: 18, padding: '32px 28px', maxWidth: 400, width: '90%', boxShadow: '0 24px 80px rgba(0,0,0,0.25)' }} onClick={e => e.stopPropagation()}>
      <div style={{ width: 56, height: 56, borderRadius: 16, background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px' }}>
        <AlertTriangle size={26} color="#ef4444" />
      </div>
      <h3 style={{ textAlign: 'center', fontSize: 18, fontWeight: 800, color: '#0f172a', margin: '0 0 8px' }}>Delete Integration?</h3>
      <p style={{ textAlign: 'center', color: '#64748b', fontSize: 13, margin: '0 0 6px', lineHeight: 1.6 }}>
        <strong>{integration.name}</strong> ko permanently delete karoge?
      </p>
      <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: 12, margin: '0 0 24px' }}>
        Yeh platform record ke saath saath <strong>sabhi companies ke shadow records</strong> bhi delete ho jaenge.
      </p>
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={onClose} style={{ flex: 1, padding: '10px', background: '#f1f5f9', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#475569' }}>Cancel</button>
        <button onClick={onConfirm} disabled={loading} style={{ flex: 1, padding: '10px', background: '#ef4444', border: 'none', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          {loading ? <Spinner size={13} color="#fff" /> : <Trash2 size={13} />}
          {loading ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// CREATE / EDIT MODAL — SUPER_ADMIN ONLY
// Creates/edits PLATFORM records
// configKeys → becomes configTemplate (field names admin needs to fill)
// config     → super_admin's own service credentials (separate from admin's)
// ─────────────────────────────────────────────────────────────────────────────
const IntegrationFormModal = ({ integration, onClose, onSaved, toast }) => {
  const isEdit = !!integration;
  const [form, setForm] = useState({
    name:        integration?.name        || '',
    slug:        integration?.slug        || '',
    category:    integration?.category    || 'Other',
    description: integration?.description || '',
    logo:        integration?.logo        || '',
    website:     integration?.website     || '',
    isPremium:   integration?.isPremium   || false,
    // configTemplate: what field keys admin should fill (comma-separated)
    configTemplate: integration?.configTemplate
      ? integration.configTemplate.join(', ')
      : (integration?.config ? Object.keys(integration.config).join(', ') : ''),
    reqPerMin:   integration?.limits?.requestsPerMinute || 60,
    reqPerDay:   integration?.limits?.requestsPerDay    || 10000,
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name required';
    if (!form.slug.trim()) e.slug = 'Slug required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);

    // configTemplate = list of field keys that admin should fill
    const templateKeys = form.configTemplate
      .split(',').map(k => k.trim()).filter(Boolean);

    // config object = blank values for super_admin's own credentials
    // (super_admin fills actual values via the Configure modal separately)
    const configObj = {};
    templateKeys.forEach(k => { configObj[k] = ''; });

    const payload = {
      name:           form.name.trim(),
      slug:           form.slug.trim().toLowerCase(),
      category:       form.category,
      description:    form.description.trim(),
      logo:           form.logo.trim(),
      website:        form.website.trim(),
      isPremium:      form.isPremium,
      configTemplate: templateKeys,
      config:         configObj,
      limits: {
        requestsPerMinute: Number(form.reqPerMin),
        requestsPerDay:    Number(form.reqPerDay),
      },
    };

    const res = isEdit
      ? await integrationsController.updateIntegration(integration._id, payload)
      : await integrationsController.createIntegration(payload);

    setSaving(false);
    if (res.success) { toast.success(res.message); onSaved(); onClose(); }
    else toast.error(res.message || 'Operation failed');
  };

  const inp = (err) => ({
    width: '100%', padding: '9px 12px',
    border: `1.5px solid ${err ? '#fca5a5' : '#e2e8f0'}`,
    borderRadius: 8, fontSize: 13, color: '#0f172a', outline: 'none',
    background: '#fff', boxSizing: 'border-box',
  });
  const lbl = { fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 5, display: 'block' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 20, backdropFilter: 'blur(4px)' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 18, width: '100%', maxWidth: 580, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 80px rgba(0,0,0,0.25)', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 11, background: isEdit ? '#eff6ff' : '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {isEdit ? <Edit3 size={18} color="#3b82f6" /> : <Plus size={18} color="#16a34a" />}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{isEdit ? 'Edit Integration' : 'Create Integration'}</div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>Platform record · Super Admin only</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 9px', background: '#fef9c3', borderRadius: 20, fontSize: 10, fontWeight: 700, color: '#92400e' }}>
            <Crown size={10} /> SUPER ADMIN
          </div>
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b' }}>
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Name *</label>
              <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Razorpay" style={inp(errors.name)}
                onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = errors.name ? '#fca5a5' : '#e2e8f0'} />
              {errors.name && <p style={{ fontSize: 11, color: '#ef4444', margin: '3px 0 0' }}>{errors.name}</p>}
            </div>
            <div>
              <label style={lbl}>Slug *</label>
              <input value={form.slug} onChange={e => set('slug', e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                placeholder="e.g. razorpay" style={inp(errors.slug)} disabled={isEdit}
                onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = errors.slug ? '#fca5a5' : '#e2e8f0'} />
              {isEdit && <p style={{ fontSize: 10, color: '#94a3b8', margin: '3px 0 0' }}>Slug edit nahi ho sakta</p>}
              {errors.slug && <p style={{ fontSize: 11, color: '#ef4444', margin: '3px 0 0' }}>{errors.slug}</p>}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Category</label>
              <select value={form.category} onChange={e => set('category', e.target.value)} style={{ ...inp(false), appearance: 'none' }}>
                {VALID_CATEGORIES.map(c => <option key={c} value={c}>{CATEGORY_META[c].icon} {c}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Logo (emoji or URL)</label>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input value={form.logo} onChange={e => set('logo', e.target.value)} placeholder="💳 or https://…" style={{ ...inp(false), flex: 1 }}
                  onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
                {form.logo && (
                  <div style={{ width: 34, height: 34, borderRadius: 8, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                    {form.logo.startsWith('http') ? <img src={form.logo} style={{ width: 22, height: 22, objectFit: 'contain' }} alt="" /> : form.logo}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <label style={lbl}>Description</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} placeholder="Short description…" rows={2}
              style={{ ...inp(false), resize: 'vertical', lineHeight: 1.6 }}
              onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
          </div>

          <div>
            <label style={lbl}>Website / Docs URL</label>
            <input value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://docs.example.com" style={inp(false)}
              onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
          </div>

          {/* Config template — what fields admin needs to fill */}
          <div>
            <label style={lbl}>Admin Config Fields (comma-separated)</label>
            <input value={form.configTemplate} onChange={e => set('configTemplate', e.target.value)}
              placeholder="apiKey, secretKey, webhookSecret" style={inp(false)}
              onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
            <p style={{ fontSize: 11, color: '#94a3b8', margin: '4px 0 0' }}>
              Ye fields admin ke "Configure Credentials" form mein dikhenge. Super admin ke apne credentials alag se Configure button se set hote hain.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Requests / Minute</label>
              <input type="number" value={form.reqPerMin} onChange={e => set('reqPerMin', e.target.value)} style={inp(false)}
                onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
            </div>
            <div>
              <label style={lbl}>Requests / Day</label>
              <input type="number" value={form.reqPerDay} onChange={e => set('reqPerDay', e.target.value)} style={inp(false)}
                onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
            </div>
          </div>

          {/* Premium toggle */}
          <div onClick={() => set('isPremium', !form.isPremium)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: form.isPremium ? '#fffbeb' : '#f8fafc', borderRadius: 10, border: `1.5px solid ${form.isPremium ? '#fde68a' : '#e2e8f0'}`, cursor: 'pointer' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <Star size={15} color={form.isPremium ? '#f59e0b' : '#94a3b8'} fill={form.isPremium ? '#f59e0b' : 'none'} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Premium Integration</div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>Requires premium subscription</div>
              </div>
            </div>
            <div style={{ width: 42, height: 22, borderRadius: 11, background: form.isPremium ? '#f59e0b' : '#e2e8f0', position: 'relative', flexShrink: 0 }}>
              <span style={{ position: 'absolute', top: 2, left: form.isPremium ? 'calc(100% - 20px)' : 2, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 8, justifyContent: 'flex-end', background: '#fafafa' }}>
          <button onClick={onClose} style={{ padding: '9px 16px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#64748b' }}>Cancel</button>
          <button onClick={handleSubmit} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 20px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {saving ? <Spinner size={13} color="#fff" /> : <Save size={13} />}
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CONFIG MODAL — admin + super_admin (fill credentials)
//
// SCOPE AWARENESS:
//   super_admin → sees/edits platform record config (own service credentials)
//   admin       → sees/edits company record config (company's own credentials)
//                 Platform config NEVER shown — only blank template fields
//
// Backend auto-creates company shadow record on first getFullConfig call
// ─────────────────────────────────────────────────────────────────────────────
const ConfigModal = ({ integration, onClose, onSaved, toast, isSuperAdmin }) => {
  const [config,       setConfig]       = useState({});
  const [show,         setShow]         = useState({});
  const [saving,       setSaving]       = useState(false);
  const [testing,      setTesting]      = useState(false);
  const [testResult,   setTestResult]   = useState(null);
  const [resetting,    setResetting]    = useState(false);
  const [loading,      setLoading]      = useState(true);
  const [scope,        setScope]        = useState(null);   // 'platform' | 'company'
  const [activeTab,    setActiveTab]    = useState('config');
  const [webhookForm,  setWebhookForm]  = useState({ event: '', url: '', secret: '' });
  const [addingWH,     setAddingWH]     = useState(false);
  const [webhooks,     setWebhooks]     = useState(integration.webhooks || []);
  const [syncSettings, setSyncSettings] = useState(integration.syncSettings || { enabled: false, frequency: 'manual' });
  const [syncSaving,   setSyncSaving]   = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const res = await integrationsController.getFullConfig(integration._id);
      if (res.success) {
        // For admin: res.data.config = company's own credentials (blank or filled)
        // For super_admin: res.data.config = platform config (own credentials)
        setConfig(res.data?.config || {});
        setScope(res.scope);
        setWebhooks(res.data?.webhooks || integration.webhooks || []);
        setSyncSettings(res.data?.syncSettings || { enabled: false, frequency: 'manual' });
      } else {
        // Fallback: use configTemplate to build blank form for admin
        const fallbackConfig = {};
        if (integration.configTemplate?.length) {
          integration.configTemplate.forEach(k => { fallbackConfig[k] = ''; });
        } else if (integration.config) {
          // config in card is already company config (masked) — use keys only
          Object.keys(integration.config).forEach(k => { fallbackConfig[k] = ''; });
        }
        setConfig(fallbackConfig);
      }
      setLoading(false);
    })();
  }, [integration._id]);

  const handleSave = async () => {
    setSaving(true);
    const res = await integrationsController.updateConfig(integration._id, config);
    setSaving(false);
    if (res.success) {
      toast.success(
        isSuperAdmin
          ? `Platform config saved (${res.scope || 'platform'})`
          : 'Company credentials saved'
      );
      onSaved(res.data);
      onClose();
    } else {
      toast.error(res.message || 'Failed to save');
    }
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    const res = await integrationsController.testConnection(integration._id);
    setTesting(false); setTestResult(res);
    res.success ? toast.success(`Connected! ${res.latency}ms`) : toast.error(res.message || 'Connection failed');
  };

  const handleReset = async () => {
    const confirmMsg = isSuperAdmin
      ? 'Platform credentials clear kar dein?'
      : 'Apni company ke saare credentials clear kar dein?';
    if (!window.confirm(confirmMsg)) return;
    setResetting(true);
    const res = await integrationsController.resetConfig(integration._id);
    setResetting(false);
    if (res.success) {
      toast.success(isSuperAdmin ? 'Platform config cleared' : 'Company config cleared');
      onSaved(res.data);
      onClose();
    } else {
      toast.error(res.message);
    }
  };

  const handleAddWebhook = async () => {
    if (!webhookForm.event || !webhookForm.url) return;
    setAddingWH(true);
    const res = await integrationsController.addWebhook(integration._id, webhookForm);
    setAddingWH(false);
    if (res.success) { setWebhooks(res.data); setWebhookForm({ event: '', url: '', secret: '' }); toast.success('Webhook added'); }
    else toast.error(res.message);
  };

  const handleRemoveWebhook = async (wid) => {
    const res = await integrationsController.removeWebhook(integration._id, wid);
    if (res.success) { setWebhooks(res.data); toast.success('Webhook removed'); }
    else toast.error(res.message);
  };

  const catMeta    = CATEGORY_META[integration.category] || CATEGORY_META.Other;
  const configKeys = Object.keys(config);

  // Scope label for UI
  const scopeLabel = isSuperAdmin
    ? { text: 'Platform Credentials', color: '#92400e', bg: '#fef9c3', icon: <Crown size={10} /> }
    : { text: 'Company Credentials',  color: '#1d4ed8', bg: '#dbeafe', icon: <ShieldCheck size={10} /> };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20, backdropFilter: 'blur(4px)' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 18, width: '100%', maxWidth: 620, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 80px rgba(0,0,0,0.25)', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 46, height: 46, borderRadius: 12, fontSize: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${catMeta.color}15`, border: `1px solid ${catMeta.color}30` }}>
            {integration.logo}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 2 }}>
              <span style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{integration.name}</span>
              {integration.isPremium && <Badge text="Premium" color="#92400e" bg="#fef3c7" />}
              {/* Scope badge — clearly shows which config is being edited */}
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 700, color: scopeLabel.color, background: scopeLabel.bg }}>
                {scopeLabel.icon} {scopeLabel.text}
              </span>
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>{integration.category} · {integration.slug}</div>
          </div>
          {testResult && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, color: testResult.success ? '#16a34a' : '#dc2626', background: testResult.success ? '#f0fdf4' : '#fef2f2', padding: '4px 10px', borderRadius: 8 }}>
              {testResult.success ? <Wifi size={12} /> : <WifiOff size={12} />}
              {testResult.success ? `${testResult.latency}ms` : 'Failed'}
            </div>
          )}
          <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b' }}>
            <X size={14} />
          </button>
        </div>

        {/* Scope info banner — admin ko clear karo ki platform config nahi dikh raha */}
        {!isSuperAdmin && (
          <div style={{ padding: '9px 22px', background: '#f0f9ff', borderBottom: '1px solid #bae6fd', display: 'flex', alignItems: 'center', gap: 7 }}>
            <Info size={12} color="#0284c7" />
            <span style={{ fontSize: 11, color: '#0369a1' }}>
              Yahan sirf <strong>aapki company ke credentials</strong> store ho rahe hain. Platform-level credentials alag managed hain.
            </span>
          </div>
        )}
        {isSuperAdmin && (
          <div style={{ padding: '9px 22px', background: '#fffbeb', borderBottom: '1px solid #fde68a', display: 'flex', alignItems: 'center', gap: 7 }}>
            <Crown size={12} color="#d97706" />
            <span style={{ fontSize: 11, color: '#92400e' }}>
              Yeh <strong>platform-level credentials</strong> hain (super_admin ke apne). Admin ki company ke credentials alag hain.
            </span>
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #f1f5f9', padding: '0 22px', background: '#fafafa' }}>
          {[
            { id: 'config',   label: 'Credentials',  icon: <Settings size={12} /> },
            { id: 'webhooks', label: `Webhooks${webhooks.length > 0 ? ` (${webhooks.length})` : ''}`, icon: <Webhook size={12} /> },
            { id: 'sync',     label: 'Sync',         icon: <RefreshCw size={12} /> },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              display: 'flex', alignItems: 'center', gap: 5, padding: '10px 13px',
              background: 'none', border: 'none',
              borderBottom: `2px solid ${activeTab === tab.id ? '#3b82f6' : 'transparent'}`,
              color: activeTab === tab.id ? '#3b82f6' : '#64748b',
              fontSize: 12, fontWeight: 600, cursor: 'pointer', marginBottom: -1,
            }}>{tab.icon}{tab.label}</button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 22px' }}>
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 140, gap: 10 }}>
              <Spinner size={22} color="#3b82f6" />
              <span style={{ fontSize: 12, color: '#94a3b8' }}>Loading credentials…</span>
            </div>
          ) : activeTab === 'config' ? (
            <>
              {configKeys.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '36px 0', color: '#94a3b8', fontSize: 13 }}>
                  <Lock size={28} color="#e2e8f0" style={{ display: 'block', margin: '0 auto 10px' }} />
                  No config fields defined. Integration form mein fields add karo.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
                  {configKeys.map(key => (
                    <div key={key}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 5 }}>
                        {isSensitive(key) && <Lock size={9} color="#f59e0b" />}
                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
                      </label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type={isSensitive(key) && !show[key] ? 'password' : 'text'}
                          value={config[key] || ''}
                          onChange={e => setConfig(p => ({ ...p, [key]: e.target.value }))}
                          placeholder={`Enter ${key}`}
                          style={{ width: '100%', padding: '9px 44px 9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#0f172a', outline: 'none', background: '#fff', boxSizing: 'border-box' }}
                          onFocus={e => e.target.style.borderColor = '#3b82f6'}
                          onBlur={e => e.target.style.borderColor = '#e2e8f0'}
                        />
                        <div style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', display: 'flex', gap: 3 }}>
                          {isSensitive(key) && (
                            <button onClick={() => setShow(p => ({ ...p, [key]: !p[key] }))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex', padding: 2 }}>
                              {show[key] ? <EyeOff size={13} /> : <Eye size={13} />}
                            </button>
                          )}
                          {config[key] && (
                            <button onClick={() => { navigator.clipboard.writeText(config[key]); toast.success('Copied!'); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex', padding: 2 }}>
                              <Copy size={12} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {integration.website && (
                <a href={integration.website} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#3b82f6', marginTop: 14, textDecoration: 'none' }}>
                  <ExternalLink size={11} /> {integration.name} documentation
                </a>
              )}
            </>
          ) : activeTab === 'webhooks' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: 14, border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>Add Webhook</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {[{ k: 'event', ph: 'Event (payment.success)' }, { k: 'url', ph: 'URL (https://…)' }, { k: 'secret', ph: 'Secret (optional)' }].map(({ k, ph }) => (
                    <input key={k} value={webhookForm[k]} onChange={e => setWebhookForm(p => ({ ...p, [k]: e.target.value }))} placeholder={ph}
                      style={{ padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 7, fontSize: 13, outline: 'none' }} />
                  ))}
                  <button onClick={handleAddWebhook} disabled={addingWH || !webhookForm.event || !webhookForm.url}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    {addingWH ? <Spinner size={12} color="#fff" /> : <Plus size={12} />} Add Webhook
                  </button>
                </div>
              </div>
              {webhooks.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8', fontSize: 13 }}>No webhooks yet</div>
              ) : webhooks.map((wh, i) => (
                <div key={wh._id || i} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '11px 13px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 9 }}>
                  <Bell size={13} color="#64748b" style={{ marginTop: 2, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{wh.event}</div>
                    <div style={{ fontSize: 11, color: '#64748b', wordBreak: 'break-all' }}>{wh.url}</div>
                  </div>
                  <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 5, background: wh.isActive ? '#dcfce7' : '#fee2e2', color: wh.isActive ? '#16a34a' : '#dc2626', fontWeight: 700 }}>
                    {wh.isActive ? 'Active' : 'Off'}
                  </span>
                  <button onClick={() => handleRemoveWebhook(wh._id)} style={{ background: '#fef2f2', border: 'none', borderRadius: 6, width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#ef4444' }}>
                    <Trash2 size={11} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div onClick={() => setSyncSettings(p => ({ ...p, enabled: !p.enabled }))} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 15px', background: syncSettings.enabled ? '#f0fdf4' : '#f8fafc', borderRadius: 10, border: `1.5px solid ${syncSettings.enabled ? '#86efac' : '#e2e8f0'}`, cursor: 'pointer' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Auto Sync</div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>Schedule par automatically sync karo</div>
                </div>
                <div style={{ width: 42, height: 22, borderRadius: 11, background: syncSettings.enabled ? '#22c55e' : '#e2e8f0', position: 'relative' }}>
                  <span style={{ position: 'absolute', top: 2, left: syncSettings.enabled ? 'calc(100% - 20px)' : 2, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                </div>
              </div>
              {syncSettings.enabled && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 7 }}>Frequency</div>
                  <div style={{ display: 'flex', gap: 7 }}>
                    {SYNC_FREQ.map(f => (
                      <button key={f} onClick={() => setSyncSettings(p => ({ ...p, frequency: f }))} style={{ flex: 1, padding: '8px', borderRadius: 7, border: `1.5px solid ${syncSettings.frequency === f ? '#3b82f6' : '#e2e8f0'}`, background: syncSettings.frequency === f ? '#eff6ff' : '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer', color: syncSettings.frequency === f ? '#1d4ed8' : '#475569', textTransform: 'capitalize' }}>
                        {f}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <button onClick={async () => {
                setSyncSaving(true);
                const r = await integrationsController.updateSyncSettings(integration._id, syncSettings);
                setSyncSaving(false);
                r.success ? toast.success('Sync settings saved') : toast.error(r.message);
              }} disabled={syncSaving} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {syncSaving ? <Spinner size={12} color="#fff" /> : <Save size={12} />}
                {syncSaving ? 'Saving…' : 'Save Sync Settings'}
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '13px 22px', borderTop: '1px solid #f1f5f9', background: '#fafafa', display: 'flex', gap: 7, alignItems: 'center' }}>
          <button onClick={handleReset} disabled={resetting} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', background: '#fff', border: '1px solid #fca5a5', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#dc2626' }}>
            {resetting ? <Spinner size={11} /> : <RotateCcw size={11} />} Reset
          </button>
          <div style={{ flex: 1 }} />
          <button onClick={handleTest} disabled={testing} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 13px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#0284c7' }}>
            {testing ? <Spinner size={12} color="#0284c7" /> : <TestTube size={12} />}
            {testing ? 'Testing…' : 'Test Connection'}
          </button>
          <button onClick={onClose} style={{ padding: '7px 13px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#64748b' }}>Cancel</button>
          {activeTab === 'config' && (
            <button onClick={handleSave} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 16px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              {saving ? <Spinner size={12} color="#fff" /> : <Save size={12} />}
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// INTEGRATION CARD
// ─────────────────────────────────────────────────────────────────────────────
const IntegrationCard = ({
  integration, onConfigure, onEdit, onDelete,
  onToggleVisibility, togglingId, viewMode, isSuperAdmin,
}) => {
  const catMeta    = CATEGORY_META[integration.category] || CATEGORY_META.Other;
  const isToggling = togglingId === integration._id;
  const health     = integration.healthStatus || 'disabled';
  const isList     = viewMode === 'list';
  const isActive   = integration.isEnabled;

  // isConfigured:
  //   super_admin card → platform record's own credentials configured
  //   admin card       → company's own credentials configured (companyRecordId exists)
  const isConfigured = integration.isConfigured;

  if (isList) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        background: '#fff', borderRadius: 10, padding: '12px 16px',
        border: isActive ? `1px solid ${catMeta.color}30` : '1px solid #f1f5f9',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        opacity: !isSuperAdmin && !isActive ? 0.5 : 1,
      }}>
        <div style={{ width: 38, height: 38, borderRadius: 9, fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${catMeta.color}12`, flexShrink: 0 }}>
          {integration.logo}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{integration.name}</span>
            {integration.isPremium && <Badge text="Pro" color="#92400e" bg="#fef3c7" />}
            {isSuperAdmin && !isActive && <Badge text="Hidden" color="#6b7280" bg="#f3f4f6" />}
            <HealthDot status={health} />
          </div>
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{integration.description}</div>
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>{integration.category}</div>
        <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
          <button onClick={() => onConfigure(integration)} style={{ padding: '5px 9px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', color: '#475569', display: 'flex', alignItems: 'center', gap: 3 }}>
            <Settings size={11} /> {isSuperAdmin ? 'Platform Config' : 'Configure'}
          </button>
          {isSuperAdmin && (
            <>
              <button onClick={() => onEdit(integration)} style={{ padding: '5px 9px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: 3 }}>
                <Edit3 size={11} /> Edit
              </button>
              <button onClick={() => onToggleVisibility(integration)} disabled={isToggling}
                style={{ padding: '5px 9px', background: isActive ? '#fef2f2' : '#f0fdf4', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', color: isActive ? '#dc2626' : '#16a34a', display: 'flex', alignItems: 'center', gap: 3 }}>
                {isToggling ? <Spinner size={10} /> : isActive ? <EyeOffIcon size={10} /> : <EyeIcon size={10} />}
                {isToggling ? '…' : isActive ? 'Deactivate' : 'Activate'}
              </button>
              <button onClick={() => onDelete(integration)} style={{ padding: '5px 8px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', color: '#dc2626', display: 'flex', alignItems: 'center' }}>
                <Trash2 size={11} />
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: '#fff', borderRadius: 14, padding: '18px',
      border: isActive ? `1.5px solid ${catMeta.color}35` : '1px solid #f1f5f9',
      boxShadow: isActive ? `0 4px 14px ${catMeta.color}12` : '0 1px 3px rgba(0,0,0,0.05)',
      position: 'relative', display: 'flex', flexDirection: 'column', gap: 11,
      opacity: isSuperAdmin ? 1 : (isActive ? 1 : 0.5),
    }}>
      {/* Top-right indicators */}
      <div style={{ position: 'absolute', top: 12, right: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
        <HealthDot status={health} />
        {integration.isPremium && <Lock size={10} color="#f59e0b" />}
        {isSuperAdmin && !isActive && (
          <span style={{ fontSize: 9, padding: '2px 6px', background: '#f3f4f6', color: '#6b7280', borderRadius: 4, fontWeight: 700 }}>HIDDEN</span>
        )}
      </div>

      {/* Logo + name */}
      <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start' }}>
        <div style={{ width: 46, height: 46, borderRadius: 12, fontSize: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${catMeta.color}15`, border: `1px solid ${catMeta.color}25`, flexShrink: 0 }}>
          {integration.logo}
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a', marginBottom: 3 }}>{integration.name}</div>
          <div style={{ fontSize: 10, color: catMeta.color, fontWeight: 700, background: `${catMeta.color}12`, padding: '2px 8px', borderRadius: 5, display: 'inline-block' }}>
            {catMeta.icon} {integration.category}
          </div>
        </div>
      </div>

      <p style={{ fontSize: 12, color: '#64748b', margin: 0, lineHeight: 1.6 }}>{integration.description}</p>

      {/* Status badges */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
        {isSuperAdmin ? (
          <Badge
            text={isActive ? '● Visible to Admins' : '○ Hidden from Admins'}
            color={isActive ? '#15803d' : '#6b7280'}
            bg={isActive ? '#dcfce7' : '#f3f4f6'}
          />
        ) : (
          <Badge text="● Active" color="#15803d" bg="#dcfce7" />
        )}
        {/* isConfigured meaning differs by role */}
        {isConfigured && (
          <Badge
            text={isSuperAdmin ? '✓ Platform Config Set' : '✓ My Config Set'}
            color="#1d4ed8" bg="#dbeafe"
          />
        )}
        {!isConfigured && (
          <Badge
            text={isSuperAdmin ? '○ Platform Config Empty' : '○ Not Configured'}
            color="#9ca3af" bg="#f9fafb"
          />
        )}
        {integration.stats?.totalRequests > 0 && (
          <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 'auto' }}>{integration.successRate ?? 100}% ↑</span>
        )}
      </div>

      {/* Super admin controls */}
      {isSuperAdmin && (
        <div style={{ display: 'flex', gap: 5, paddingTop: 3, borderTop: '1px dashed #f1f5f9' }}>
          <button onClick={() => onEdit(integration)} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '7px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: 'pointer', color: '#1d4ed8' }}>
            <Edit3 size={11} /> Edit
          </button>
          <button onClick={() => onToggleVisibility(integration)} disabled={isToggling} style={{ flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '7px', border: 'none', borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: 'pointer', background: isActive ? '#fef2f2' : '#f0fdf4', color: isActive ? '#dc2626' : '#16a34a' }}>
            {isToggling ? <Spinner size={11} color={isActive ? '#dc2626' : '#16a34a'} />
              : isActive ? <EyeOffIcon size={11} /> : <EyeIcon size={11} />}
            {isToggling ? '…' : isActive ? 'Deactivate (Hide)' : 'Activate (Show)'}
          </button>
          <button onClick={() => onDelete(integration)} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '7px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 7, fontSize: 11, fontWeight: 700, cursor: 'pointer', color: '#dc2626' }}>
            <Trash2 size={11} /> Delete
          </button>
        </div>
      )}

      {/* Configure button — both roles */}
      <div style={{ paddingTop: isSuperAdmin ? 0 : 4, borderTop: isSuperAdmin ? 'none' : '1px solid #f8fafc' }}>
        <button onClick={() => onConfigure(integration)} style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
          padding: '9px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
          background: isSuperAdmin ? '#fffbeb' : '#f8fafc',
          border: isSuperAdmin ? '1px solid #fde68a' : '1px solid #e2e8f0',
          color: isSuperAdmin ? '#92400e' : '#374151',
        }}>
          {isSuperAdmin ? <Crown size={12} /> : <Settings size={12} />}
          {isSuperAdmin ? 'Platform Credentials' : 'Configure My Credentials'}
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// STAT CARD
// ─────────────────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, icon, color, sub }) => (
  <div style={{ background: '#fff', borderRadius: 12, padding: '16px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', border: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 13 }}>
    <div style={{ width: 42, height: 42, borderRadius: 11, background: `${color}14`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {React.cloneElement(icon, { size: 19, color })}
    </div>
    <div>
      <div style={{ fontSize: 24, fontWeight: 900, color: '#0f172a', lineHeight: 1 }}>{value ?? '—'}</div>
      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: 10, color, fontWeight: 700, marginTop: 1 }}>{sub}</div>}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────
export default function IntegrationsPage() {
  const [currentUser,      setCurrentUser]      = useState(null);
  const [integrations,     setIntegrations]     = useState([]);
  const [stats,            setStats]            = useState({ total: 0, enabled: 0, configured: 0, premium: 0 });
  const [statsScope,       setStatsScope]       = useState(null);
  const [categories,       setCategories]       = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery,      setSearchQuery]      = useState('');
  const [viewMode,         setViewMode]         = useState('grid');
  const [configModal,      setConfigModal]      = useState(null);
  const [formModal,        setFormModal]        = useState(null);
  const [deleteTarget,     setDeleteTarget]     = useState(null);
  const [deletingId,       setDeletingId]       = useState(null);
  const [togglingId,       setTogglingId]       = useState(null);
  const [loading,          setLoading]          = useState(true);
  const [seeding,          setSeeding]          = useState(false);
  const [refreshing,       setRefreshing]       = useState(false);
  const [filterStatus,     setFilterStatus]     = useState('all');
  const [filterConfigured, setFilterConfigured] = useState('all');
  const [showFilters,      setShowFilters]      = useState(false);
  const [bulkSelected,     setBulkSelected]     = useState([]);
  const [bulkAction,       setBulkAction]       = useState('enable');
  const [bulkLoading,      setBulkLoading]      = useState(false);

  const { toasts, toast, remove } = useToast();
  const searchTimer = useRef(null);

  // ── Auth ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const u = integrationsController.getCurrentUser?.()
      || JSON.parse(localStorage.getItem('user') || 'null');
    setCurrentUser(u);
  }, []);

  const isSuperAdmin = currentUser?.role === 'super_admin';
  const isAdmin      = isSuperAdmin || currentUser?.role === 'admin';

  // ── Load ──────────────────────────────────────────────────────────────────
  const loadAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const opts = {};
      if (selectedCategory !== 'All') opts.category = selectedCategory;
      if (searchQuery)                opts.search   = searchQuery;
      // enabled filter only meaningful for super_admin (admin always sees only enabled)
      if (isSuperAdmin && filterStatus !== 'all') opts.enabled = filterStatus === 'active';
      if (filterConfigured !== 'all') opts.configured = filterConfigured === 'configured';

      const [intRes, statRes, catRes] = await Promise.all([
        integrationsController.getIntegrations(opts),
        integrationsController.getStatistics(),
        integrationsController.getCategories(),
      ]);

      if (intRes.success)  setIntegrations(intRes.data);
      if (statRes.success) { setStats(statRes.data); setStatsScope(statRes.scope); }
      if (catRes.success)  setCategories(catRes.data);
    } catch { toast.error('Failed to load integrations'); }
    finally { setLoading(false); setRefreshing(false); }
  }, [selectedCategory, searchQuery, filterStatus, filterConfigured, isSuperAdmin]);

  useEffect(() => { if (currentUser) loadAll(false); }, [currentUser, loadAll]);

  const handleSearch = val => {
    setSearchQuery(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => loadAll(true), 400);
  };

  // ── Seed — SUPER_ADMIN ONLY ───────────────────────────────────────────────
  const handleSeed = async () => {
    setSeeding(true);
    const res = await integrationsController.seedIntegrations();
    setSeeding(false);
    if (res.success) {
      toast.success(`${res.data?.created ?? 0} platform integrations created, ${res.data?.skipped ?? 0} already existed`);
      loadAll(true);
    } else toast.error(res.message || 'Seed failed');
  };

  // ── Toggle Visibility — SUPER_ADMIN ONLY ─────────────────────────────────
  // Changes platform record isEnabled → affects ALL admins globally
  const handleToggleVisibility = async (integration) => {
    setTogglingId(integration._id);
    const res = await integrationsController.toggleIntegration(integration._id);
    setTogglingId(null);
    if (res.success) {
      setIntegrations(prev =>
        prev.map(i => i._id === integration._id ? { ...i, isEnabled: res.isEnabled } : i)
      );
      setStats(prev => ({
        ...prev,
        enabled: res.isEnabled ? prev.enabled + 1 : Math.max(0, prev.enabled - 1),
      }));
      toast.success(res.message);
    } else toast.error(res.message || 'Toggle failed');
  };

  // ── Delete — SUPER_ADMIN ONLY ─────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeletingId(deleteTarget._id);
    const res = await integrationsController.deleteIntegration(deleteTarget._id);
    setDeletingId(null);
    if (res.success) {
      toast.success(res.message);
      setIntegrations(prev => prev.filter(i => i._id !== deleteTarget._id));
      setStats(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
      setDeleteTarget(null);
    } else toast.error(res.message || 'Delete failed');
  };

  // ── Bulk — SUPER_ADMIN ONLY ───────────────────────────────────────────────
  const handleBulk = async () => {
    if (!bulkSelected.length) return;
    setBulkLoading(true);
    const res = await integrationsController.bulkUpdate(bulkSelected, bulkAction);
    setBulkLoading(false);
    if (res.success) { toast.success(res.message); setBulkSelected([]); loadAll(true); }
    else toast.error(res.message);
  };

  const toggleBulkSelect = id =>
    setBulkSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // ── Config saved ──────────────────────────────────────────────────────────
  // updatedData comes from the backend response — could be company or platform record
  const handleConfigSaved = updatedData => {
    if (updatedData) {
      setIntegrations(prev => prev.map(i => {
        // Match by _id (platform record id) OR by slug if it's a company record response
        if (i._id === updatedData._id || i._id === updatedData.platformIntegrationId) {
          return { ...i, isConfigured: updatedData.isConfigured ?? i.isConfigured };
        }
        return i;
      }));
    }
    loadAll(true);
  };

  // ── Access guard ──────────────────────────────────────────────────────────
  if (currentUser && !isAdmin) {
    return (
      <div style={{ padding: 32, background: '#f8fafc', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: '#fff', border: '1.5px solid #fca5a5', borderRadius: 16, padding: 32, maxWidth: 380, textAlign: 'center' }}>
          <AlertCircle size={36} color="#ef4444" style={{ marginBottom: 12 }} />
          <h3 style={{ fontSize: 17, fontWeight: 800, color: '#991b1b', marginBottom: 6 }}>Access Denied</h3>
          <p style={{ color: '#7f1d1d', fontSize: 13 }}>Sirf Admins integrations manage kar sakte hain.</p>
        </div>
      </div>
    );
  }

  const catList = ['All', ...categories.map(c => c.name)];

  return (
    <div style={{ padding: '24px 28px', background: '#f8fafc', minHeight: '100vh', fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <style>{`
        @keyframes spin    { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes slideIn { from { transform: translateX(20px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes fadeIn  { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .int-card:hover { transform: translateY(-2px) !important; box-shadow: 0 8px 22px rgba(0,0,0,0.09) !important; }
      `}</style>

      <ToastBox toasts={toasts} remove={remove} />

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 5 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Plug size={18} color="#fff" />
            </div>
            <h1 style={{ fontSize: 24, fontWeight: 900, color: '#0f172a', margin: 0, letterSpacing: '-0.5px' }}>Integrations</h1>
            {isSuperAdmin && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 9px', background: '#fef9c3', borderRadius: 20, fontSize: 10, fontWeight: 800, color: '#92400e' }}>
                <Crown size={10} /> SUPER ADMIN · PLATFORM
              </div>
            )}
          </div>
          <p style={{ fontSize: 13, color: '#64748b', margin: 0, paddingLeft: 51 }}>
            {isSuperAdmin
              ? `${stats.total} platform integrations · ${stats.enabled} active (visible to all admins) · ${stats.configured} platform configured`
              : `${stats.total} services available · ${stats.configured} configured by your company`}
          </p>
        </div>

        <div style={{ display: 'flex', gap: 7 }}>
          {isSuperAdmin && (
            <>
              <button onClick={() => setFormModal('create')} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 14px', background: '#0f172a', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', color: '#fff' }}>
                <Plus size={12} /> New Integration
              </button>
              <button onClick={handleSeed} disabled={seeding} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 13px', background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', color: '#374151' }}>
                {seeding ? <Spinner size={12} /> : <Package size={12} />}
                {seeding ? 'Seeding…' : 'Seed Defaults'}
              </button>
            </>
          )}
          <button onClick={() => loadAll(true)} disabled={refreshing} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 12px', background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 12, cursor: 'pointer', color: '#374151' }}>
            <RefreshCw size={12} style={{ animation: refreshing ? 'spin 0.8s linear infinite' : 'none' }} />
          </button>
        </div>
      </div>

      {/* ── Stats ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        <StatCard label={isSuperAdmin ? 'Platform Total' : 'Available'} value={stats.total}      icon={<Plug />}        color="#6366f1" />
        <StatCard
          label={isSuperAdmin ? 'Activated (Visible to All Admins)' : 'Active'}
          value={stats.enabled}
          icon={<Zap />} color="#10b981"
          sub={stats.total ? `${Math.round((stats.enabled / stats.total) * 100)}%` : ''}
        />
        <StatCard
          label={isSuperAdmin ? 'Platform Configured' : 'My Company Configured'}
          value={stats.configured}
          icon={<ShieldCheck />} color="#3b82f6"
        />
        <StatCard label="Premium" value={stats.premium} icon={<Activity />} color="#f59e0b" />
      </div>

      {/* ── Super admin info banner ── */}
      {isSuperAdmin && (
        <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <Crown size={16} color="#fbbf24" />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#f1f5f9' }}>Platform Management</div>
            <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.6 }}>
              <strong style={{ color: '#4ade80' }}>Activate</strong> → card sabhi admins ko globally visible hoga.
              <strong style={{ color: '#f87171' }}> Deactivate</strong> → sabhi admins se hidden ho jaega.
              <span style={{ color: '#fbbf24' }}> "Platform Credentials"</span> = aapke apne service credentials (admins ko nahi dikhte).
            </div>
          </div>
          <div style={{ fontSize: 11, color: '#64748b', textAlign: 'right' }}>
            <div style={{ color: '#4ade80', fontWeight: 700 }}>{stats.enabled} active</div>
            <div style={{ color: '#94a3b8' }}>{stats.total - stats.enabled} hidden</div>
          </div>
        </div>
      )}

      {/* ── Admin info banner ── */}
      {!isSuperAdmin && (
        <div style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%)', borderRadius: 10, padding: '10px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #bfdbfe' }}>
          <Info size={14} color="#3b82f6" />
          <div style={{ fontSize: 11, color: '#1e40af', lineHeight: 1.5 }}>
            Yahan <strong>sirf aapki company ke credentials</strong> configure hote hain. Platform-level configuration alag manage hota hai.
            Har integration ke liye "Configure My Credentials" click karein.
          </div>
        </div>
      )}

      {/* ── Bulk bar (super_admin) ── */}
      {isSuperAdmin && bulkSelected.length > 0 && (
        <div style={{ background: '#0f172a', borderRadius: 10, padding: '11px 16px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10, animation: 'fadeIn 0.15s ease' }}>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>{bulkSelected.length} selected</span>
          <div style={{ display: 'flex', gap: 5 }}>
            {['enable', 'disable'].map(a => (
              <button key={a} onClick={() => setBulkAction(a)} style={{ padding: '4px 11px', borderRadius: 5, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, background: bulkAction === a ? '#fff' : 'transparent', color: bulkAction === a ? '#0f172a' : '#64748b', textTransform: 'capitalize' }}>
                {a === 'enable' ? 'Activate (Show All)' : 'Deactivate (Hide All)'}
              </button>
            ))}
          </div>
          <button onClick={handleBulk} disabled={bulkLoading} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            {bulkLoading ? <Spinner size={11} color="#fff" /> : <Layers size={11} />}
            Apply to {bulkSelected.length}
          </button>
          <button onClick={() => setBulkSelected([])} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
            <X size={13} />
          </button>
        </div>
      )}

      {/* ── Filter bar ── */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '14px 18px', marginBottom: 18, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <Search size={14} color="#94a3b8" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
            <input type="text" placeholder="Search integrations…" value={searchQuery} onChange={e => handleSearch(e.target.value)}
              style={{ width: '100%', padding: '9px 12px 9px 36px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#0f172a', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 7, padding: 2 }}>
            {[['grid', <Grid3X3 size={13} />], ['list', <List size={13} />]].map(([m, ic]) => (
              <button key={m} onClick={() => setViewMode(m)} style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, border: 'none', cursor: 'pointer', background: viewMode === m ? '#fff' : 'transparent', color: viewMode === m ? '#0f172a' : '#94a3b8', boxShadow: viewMode === m ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}>
                {ic}
              </button>
            ))}
          </div>
          {isSuperAdmin && (
            <button onClick={() => setShowFilters(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 13px', background: showFilters ? '#0f172a' : '#fff', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', color: showFilters ? '#fff' : '#374151' }}>
              <Filter size={12} /> Filters
              {(filterStatus !== 'all' || filterConfigured !== 'all') && (
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} />
              )}
            </button>
          )}
        </div>

        {/* Super admin filters */}
        {isSuperAdmin && showFilters && (
          <div style={{ display: 'flex', gap: 12, marginBottom: 12, padding: '10px 0', borderTop: '1px solid #f8fafc', borderBottom: '1px solid #f8fafc', flexWrap: 'wrap', animation: 'fadeIn 0.15s ease' }}>
            {[
              { label: 'Visibility', state: filterStatus, setter: setFilterStatus, opts: [['all','All'], ['active','Active (visible)'], ['inactive','Hidden']] },
              { label: 'Platform Config', state: filterConfigured, setter: setFilterConfigured, opts: [['all','All'], ['configured','Platform Configured'], ['not','Not Configured']] },
            ].map(({ label, state, setter, opts }) => (
              <div key={label}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 5 }}>{label}</div>
                <div style={{ display: 'flex', gap: 4 }}>
                  {opts.map(([v, l]) => (
                    <button key={v} onClick={() => { setter(v); setTimeout(() => loadAll(true), 80); }} style={{ padding: '4px 10px', borderRadius: 5, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, background: state === v ? '#0f172a' : '#f1f5f9', color: state === v ? '#fff' : '#475569' }}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {(filterStatus !== 'all' || filterConfigured !== 'all') && (
              <button onClick={() => { setFilterStatus('all'); setFilterConfigured('all'); setTimeout(() => loadAll(true), 80); }} style={{ alignSelf: 'flex-end', padding: '4px 9px', background: '#fef2f2', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer', color: '#dc2626' }}>
                Clear
              </button>
            )}
          </div>
        )}

        {/* Category tabs */}
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {catList.map(cat => {
            const meta   = CATEGORY_META[cat] || CATEGORY_META.Other;
            const isAct  = selectedCategory === cat;
            const catDat = categories.find(c => c.name === cat);
            return (
              <button key={cat} onClick={() => { setSelectedCategory(cat); setTimeout(() => loadAll(true), 50); }} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 11px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 700, background: isAct ? meta.color : '#f1f5f9', color: isAct ? '#fff' : '#475569' }}>
                {meta.icon} {cat}
                {catDat && <span style={{ fontSize: 9, background: isAct ? 'rgba(255,255,255,0.25)' : '#e2e8f0', padding: '1px 5px', borderRadius: 4, fontWeight: 800, color: isAct ? '#fff' : '#64748b' }}>{catDat.total}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Content ── */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 280, gap: 10 }}>
          <Spinner size={28} color="#3b82f6" />
          <span style={{ fontSize: 13, color: '#94a3b8' }}>Loading integrations…</span>
        </div>
      ) : integrations.length === 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 280, gap: 13, background: '#fff', borderRadius: 14, border: '1px solid #f1f5f9' }}>
          <Plug size={36} color="#e2e8f0" />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#94a3b8', marginBottom: 5 }}>
              {isSuperAdmin ? 'Koi platform integration nahi mili' : 'Abhi koi integration active nahi hai'}
            </div>
            <div style={{ fontSize: 12, color: '#cbd5e1' }}>
              {isSuperAdmin
                ? (searchQuery ? `"${searchQuery}" ke liye kuch nahi mila` : 'Seed karo ya nayi integration create karo')
                : 'Super Admin se contact karo integrations activate karwane ke liye'}
            </div>
          </div>
          {isSuperAdmin && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setFormModal('create')} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '9px 16px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                <Plus size={12} /> New Integration
              </button>
              <button onClick={handleSeed} disabled={seeding} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '9px 16px', background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                {seeding ? <Spinner size={12} /> : <Package size={12} />}
                {seeding ? 'Seeding…' : 'Seed Defaults'}
              </button>
            </div>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: 14, animation: 'fadeIn 0.2s ease' }}>
          {integrations.map(int => (
            <div key={int._id} className="int-card" style={{ transition: 'transform 0.15s, box-shadow 0.15s', position: 'relative' }}>
              {isSuperAdmin && (
                <input type="checkbox" checked={bulkSelected.includes(int._id)} onChange={() => toggleBulkSelect(int._id)}
                  style={{ position: 'absolute', top: 10, left: 10, zIndex: 3, accentColor: '#3b82f6', width: 14, height: 14, cursor: 'pointer' }} />
              )}
              <IntegrationCard
                integration={int}
                onConfigure={setConfigModal}
                onEdit={i => setFormModal(i)}
                onDelete={setDeleteTarget}
                onToggleVisibility={handleToggleVisibility}
                togglingId={togglingId}
                viewMode="grid"
                isSuperAdmin={isSuperAdmin}
              />
            </div>
          ))}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, animation: 'fadeIn 0.2s ease' }}>
          {integrations.map(int => (
            <div key={int._id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {isSuperAdmin && (
                <input type="checkbox" checked={bulkSelected.includes(int._id)} onChange={() => toggleBulkSelect(int._id)}
                  style={{ accentColor: '#3b82f6', width: 14, height: 14, cursor: 'pointer', flexShrink: 0 }} />
              )}
              <div style={{ flex: 1 }}>
                <IntegrationCard
                  integration={int}
                  onConfigure={setConfigModal}
                  onEdit={i => setFormModal(i)}
                  onDelete={setDeleteTarget}
                  onToggleVisibility={handleToggleVisibility}
                  togglingId={togglingId}
                  viewMode="list"
                  isSuperAdmin={isSuperAdmin}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {integrations.length > 0 && (
        <div style={{ textAlign: 'center', padding: '16px 0 0', fontSize: 11, color: '#94a3b8' }}>
          {integrations.length} integration{integrations.length !== 1 ? 's' : ''}
          {selectedCategory !== 'All' ? ` in ${selectedCategory}` : ''}
          {searchQuery ? ` — "${searchQuery}"` : ''}
        </div>
      )}

      {/* ── Modals ── */}
      {configModal && (
        <ConfigModal
          integration={configModal}
          onClose={() => setConfigModal(null)}
          onSaved={handleConfigSaved}
          toast={toast}
          isSuperAdmin={isSuperAdmin}
        />
      )}
      {formModal && (
        <IntegrationFormModal
          integration={formModal === 'create' ? null : formModal}
          onClose={() => setFormModal(null)}
          onSaved={() => loadAll(true)}
          toast={toast}
        />
      )}
      {deleteTarget && (
        <DeleteModal
          integration={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          loading={deletingId === deleteTarget._id}
        />
      )}
    </div>
  );
}